import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal } from '@angular/core';
import { Router } from '@angular/router';
import { PageComponent, PageHeaderComponent } from '@softapps/core/pages';
import {
  backNavigation,
  queryParam,
  SortHeaderDirective,
  title } from '@softline/application';
import { base64Decode, base64Encode, isObjectSort, Sort } from '@softline/core';
import { modalFn} from '@softline/ui-core';
import { FetteinheitenKalkulatorComponent } from '../../components/fetteinheiten-kalkulator/fetteinheiten-kalkulator.component';
import LagerCardComponent from '../../components/product-card/lager-card.component';
import { Detail } from '../../data/detail';
import { Lagerliste } from '../../data/lagerliste';
import { Artikel } from '../../data/product';
import { Verbrauch } from '../../data/verbrauch';
import { Zeitraum } from '../../data/zeitraum';
import { EditZeitraumFormDialogComponent } from '../../dialogs/edit-zeitraum-form-dialog/edit-zeitraum-form-dialog.component';
import { RohstoffbereitstellungDetailService } from '../../services/detail.service';
import { FetteinheitenRechnerService } from '../../services/fetteinheiten-rechner.service';
import { LagerlistenVerbrauchService } from '../../services/lagerlisten-verbrauch.service';
import { MilchartikelService } from '../../services/milchartikel.service';
import { QueryDetailService, QueryService } from '../../services/query.service';

import { FormatPipe } from '@softline/ui-core';
import { IconComponent } from '@softline/ui-core';
import { SelectBoxComponent } from '@softline/ui-core/select';
import { SelectBoxOptionDirective } from '@softline/ui-core/select';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-produktion-rohstoffbereitstellung-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.html'],
  imports: [
    CommonModule,
    PageComponent,
    PageHeaderComponent,
    LagerCardComponent,
    FetteinheitenKalkulatorComponent,
    SortHeaderDirective,
    FormatPipe,
    IconComponent,
    SelectBoxComponent,
    TranslatePipe,
    SelectBoxOptionDirective],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush })
export default class DashboardComponent {
  private readonly queryService = inject(QueryService);
  private readonly queryDetailService = inject(QueryDetailService);

  private readonly milchartikelService = inject(MilchartikelService);
  private readonly lagerlistenVerbrauchService = inject(LagerlistenVerbrauchService);
  private readonly fetteinheitenRechnerService = inject(FetteinheitenRechnerService);
  private readonly detailService = inject(RohstoffbereitstellungDetailService);

  readonly modal = modalFn();

  readonly selectedRow = signal<Verbrauch | null>(null);

  readonly sort = signal<Sort<Verbrauch> | null>(null);
  readonly sortDetail = signal<Sort<Detail> | null>(null);

  readonly artikel = this.milchartikelService.all;

  readonly details = this.detailService.all;
  readonly kalkulatorZeilen = this.fetteinheitenRechnerService.all;

  readonly base64Query = queryParam('query');

  readonly jsonQuery = computed(() => {
    const query = this.base64Query();
    if (!query) return undefined;
    return JSON.parse(base64Decode(query));
  });

  readonly query = computed(() => {
    const query = this.jsonQuery();
    const artikel = this.artikel();

    return {
      vondatum: query?.['vondatum'],
      bisdatum: query?.['bisdatum'],
      artikel: artikel.find((o) => o.id === query?.['idartstamm']) };
  });

  readonly selectedArtikel = computed(() => {
    const query = this.query();
    return query.artikel;
  });

  readonly lagerlistenVerbrauch = this.lagerlistenVerbrauchService.value;

  readonly lagerlisten = computed(
    () => (this.lagerlistenVerbrauch()?.['lagerliste'] ?? []) as Lagerliste[]
  );

  readonly verbraeuche = computed(() => {
    const verbraeuche = this.lagerlistenVerbrauch()?.['verbraeuche'] ?? [];
    const sort = this.sort();

    if (!sort || !isObjectSort(sort)) return verbraeuche;

    const key = sort?.property;

    return verbraeuche.sort((a, b) => {
      if (sort.direction === 'desc') {
        return b?.werte?.[key] > a?.werte?.[key] ? 1 : -1;
      } else {
        return a?.werte?.[key] > b?.werte?.[key] ? 1 : -1;
      }
    });
  });

  readonly sortedDetails = computed(() => {
    const details = this.details() ?? [];
    const sort = this.sortDetail();

    if (!sort) return details;

    const key = sort.property;

    if (key === 'bezeichnung') {
      return details.sort((a, b) => {
        return sort.direction === 'desc'
          ? a.artikel?.bezeichnung?.localeCompare(b.artikel?.bezeichnung)
          : b.artikel?.bezeichnung?.localeCompare(a.artikel?.bezeichnung);
      });
    }

    return details.sort((a, b) => {
      if (sort.direction === 'desc') {
        return b?.werte?.[key] > a?.werte?.[key] ? 1 : -1;
      } else {
        return a?.werte?.[key] > b?.werte?.[key] ? 1 : -1;
      }
    });
  });

  readonly #detailQuery = computed(() => {
    const selectedRow = this.selectedRow();
    const selectedArtikel = this.selectedArtikel();

    if (!selectedArtikel || !selectedRow || !selectedRow?.datum) return undefined;
    return {
      datum: selectedRow?.datum,
      artikel: selectedArtikel };
  });

  constructor(private readonly router: Router) {
    title('Rohstoffbereitstellung');
    backNavigation('/');
    this.queryService.registerQuery(this.query);
    this.queryDetailService.registerQuery(this.#detailQuery);
  }

  async onArtikelChanged(artikel: Artikel): Promise<void> {
    await this.updateQuery(this.query(), artikel.id);
  }

  async editZeitraum(): Promise<void> {
    const result:
      | { vondatum: string | null; bisdatum: string | null }
      | 'DISMISSED'
      | null = await this.modal({
      component: EditZeitraumFormDialogComponent,
      dismiss: true,
      data: {
        vondatum: this.query()?.['vondatum'] ?? null,
        bisdatum: this.query()?.['bisdatum'] ?? null } });

    if (!result || result === 'DISMISSED' || !result?.vondatum || !result?.bisdatum)
      return;

    await this.updateQuery(result);
  }

  private async updateQuery(zeitraum: Zeitraum, idartstamm?: number): Promise<void> {
    const currentQuery = this.query();

    const query = base64Encode(
      JSON.stringify({
        vondatum: zeitraum?.vondatum,
        bisdatum: zeitraum?.bisdatum,
        idartstamm: idartstamm ? idartstamm : currentQuery?.artikel?.id })
    );

    await this.router.navigate(['/rohstoffbereitstellung'], {
      queryParams: { query } });
  }

  selectRow(row: Verbrauch): void {
    this.selectedRow.set(row);
  }

  sortChanged(event: Sort<Verbrauch> | null): void {
    this.sort.set(event);
  }

  sortDetailChanged(event: Sort<Detail> | null): void {
    this.sortDetail.set(event);
  }
}
